import React from 'react';
import useReveal from '../../hooks/useReveal';

const AcceptableUsePolicy = () => {
  document.title = 'Acceptable Use Policy - Cloudvantage';
  const [headingRef, headingVisible] = useReveal();
  return (
    <div className="max-w-4xl mx-auto py-10 px-4 md:px-6 text-black dark:text-white">
      <h1 ref={headingRef} className={`text-xl font-bold mb-6 ${headingVisible ? 'animate-reveal' : 'opacity-0'}`}>Acceptable Use Policy (AUP)</h1>

      <section className="mb-8">
        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li className="mb-4">
            The following <strong>ACCEPTABLE USE POLICY ("AUP")</strong>{' '}
            supplements and is subject to Cloudvantage, ("Company") Universal{' '}
            <a
              href="/terms-of-use"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              Universal Terms of Use
            </a>{' '}
            ("UToU"), which are incorporated herein by this reference.
            Capitalized terms used and not otherwise defined herein shall have
            the meanings set forth in the{' '}
            <a
              href="/terms-of-use"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              UToU
            </a>
            . In the event of a conflict between the AUP and the{' '}
            <a
              href="/terms-of-use"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              UToU
            </a>
            , this AUP shall control. Customer acknowledges that Customer has
            read this AUP.
          </li>
          <li className="mb-4">
            Subject to Company's{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              Privacy Policy
            </a>
            , Company does not monitor any content that is submitted to, stored
            on, or distributed or disseminated by Customer via the Products or
            Customer's website(s), Customer's customer(s) and/or Customer's
            user(s) (collectively, the "Customer Content"). By implementing,
            using, posting or publishing Customer Content to the Website(s) or
            through the Products, Customer represents and warrants to Company
            that (a) Customer has all necessary rights to use and distribute
            Customer Content and (b) Customer Content does not violate the
            rights of any third party(ies).
          </li>
          <li className="mb-4">
            The Websites and Products may only be used for lawful purposes under
            this AUP and the{' '}
            <a
              href="/terms-of-use"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              UToU
            </a>
            .
          </li>
          <li className="mb-4">
            Customer is prohibited from using the Websites or Products:
            <ol className="list-decimal pl-6 mt-2 mb-2">
              <li className="mb-4">
                In any manner that violates any federal, state, international,
                or local law or regulation;
              </li>
              <li className="mb-4">
                In any manner that violates any other applicable policy
                maintained by Company, including without limitation the{' '}
                <a
                  href="/terms-of-use"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  UToU
                </a>{' '}
                or{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  Privacy Policy
                </a>
                ;
              </li>
              <li className="mb-4">
                In a manner that interferes with Company's activities or
                business, interferes with Company's delivery of the Products or
                harms any of Company's customers, affiliates, directors,
                officers, employees, partners, brands, or Products;
              </li>
              <li className="mb-4">
                To threaten, bypass, or destroy any security mechanism used with
                or incorporated in the Websites or Products;
              </li>
              <li className="mb-4">
                To disparage, denigrate, insult, or otherwise make pejorative
                statements about Company and/or any of its affiliates,
                directors, officers, employees, partners, brands, or Products;
              </li>
              <li className="mb-4">
                To transmit any materials, including without limitation any
                "spam," "junk mail," or other deceptive or unsolicited bulk or
                commercial email;
              </li>
              <li className="mb-4">To violate the rights of others;</li>
              <li className="mb-4">
                To harm, or attempt to harm, minors in any way;
              </li>
              <li className="mb-4">For any illegal or fraudulent activity;</li>
              <li className="mb-4">
                For any content or activity that promotes child sexual
                exploitation or abuse;
              </li>
              <li className="mb-4">
                To violate the security, integrity, or availability of any user,
                network, computer or communications system, software
                application, or network or computing device;
              </li>
              <li className="mb-4">
                To modify, sub-license, adapt, translate, reverse engineer,
                sell, disassemble, or decompile any portion of a Product and/or
                the Websites' functionality;
              </li>
              <li className="mb-4">
                To impersonate Company (including any Company personnel), any
                user of the Websites and/or Products, or any other person or
                entity;
              </li>
              <li className="mb-4">
                To transmit any content, data, or material that infringes upon
                the intellectual property rights or other personal rights
                (including privacy) of any third parties;
              </li>
              <li className="mb-4">
                To use the Products and/or Websites in connection with
                unsolicited, unwanted, or harassing communications, including
                without limitation, SMS messages, phone calls, voice mail, chat,
                video, or email;
              </li>
              <li className="mb-4">
                To transmit any content, data, or material that contains
                viruses, spyware, spiders, robots, worms, Trojan horses, logic
                bombs, or any other type of malicious or deleterious programs;
              </li>
              <li className="mb-4">
                To use the Websites or Products to collect information about
                other users, including telephone numbers or email addresses,
                without any such individual's prior explicit consent or
                otherwise under false pretenses of any kind;
              </li>
              <li className="mb-4">
                To use the Products or Websites to record or monitor a telephone
                call, virtual communication or any other communication without
                first obtaining express consent from the participants per
                applicable law;
              </li>
              <li className="mb-4">
                In any manner that can reasonably be considered contrary to the
                Company's goodwill, public image, and overall reputation;
              </li>
              <li className="mb-4">
                To express or imply that any user's statements, activities, or
                causes are endorsed by Company;
              </li>
              <li className="mb-4">
                To collect, or attempt to collect, personal information about
                users or other third parties without their prior knowledge and
                affirmative consent;
              </li>
              <li className="mb-4">
                To divulge any of Company's (or any of its affiliate's)
                proprietary information;
              </li>
              <li className="mb-4">
                In any manner that promotes any illegal activity;
              </li>
              <li className="mb-4">
                To post and/or store material that promotes, incites, or
                facilitates acts of violence, hate speech, terrorism or other
                serious harm;
              </li>
              <li className="mb-4">
                To publish or post the personal information of a person or
                entity with the purpose of inciting violence or harassment
                toward that individual or entity ("doxxing');
              </li>
              <li className="mb-4">
                To use or implement cryptocurrency mining, crypto mining, render
                farming, committing or promoting any type of illegal activity
                including, but not limited to, fraud, mailbombing, denial of
                service attacks, storing and/or housing and/or linking to
                illegal content, including but not limited to, "warez,"
                "hacking," "cracking," "key generators," gambling, obscene
                material or be used in the traffic of illegal materials;
              </li>
              <li className="mb-4">
                To use or post banner advertisement services for display on
                other websites or devices (commercial banner ad rotation);
              </li>
              <li className="mb-4">
                To use or post dump or mirror scripts that allow an anonymous
                user to upload a file for others to download;
              </li>
              <li className="mb-4">
                To use or post push button mail scripts that allow the user to
                specify recipient email addresses;
              </li>
              <li className="mb-4">
                To use or post anonymous or bulk SMS gateways;
              </li>
              <li className="mb-4">To use or post BitTorrent trackers;</li>
              <li className="mb-4">
                To use or post any script or application that causes a
                degradation in the performance of our servers or network
                environments; or
              </li>
              <li className="mb-4">
                To use or post ad-servers, attempts to circumvent quota systems
                owned by "nobody," certain podcasting sites, use of torrent
                software, proxies, excessive resource usage or "core dumping,"
                attempting to circumvent any of Company's or other websites
                security policies, procedures, or systems.
              </li>
            </ol>
          </li>
          <li className="mb-4">
            Customer understands and agrees that Company, in its sole and
            absolute discretion, determines whether any information transmitted
            or received violates this Section and may investigate any suspected
            violation of this AUP or the UToS, and remove or disable access to
            any content or resource that violates this AUP, including Customer
            Content. Customer agrees to cooperate with Company to remedy any
            violation and cooperate with any law enforcement agency. When
            determining whether there has been a violation, Company may consider
            Customer's ability and willingness to comply with this AUP,
            including the policies and processes Customer has in place to
            prevent or identify and remove any prohibited content or activity.
          </li>
          <li className="mb-4">
            Customer shall be solely responsible, will be held liable and shall
            indemnify and hold Company and its employees, officers, directors
            and agents harmless from any claim or demand, including reasonable
            attorneys' fees, made by a third party arising from Customer's
            breach of this AUP and any and all of Customer Content that is
            submitted through a Customer Account.
          </li>
          <li className="mb-4">
            Customer shall be solely responsible and will be held liable and
            shall indemnify and hold Company and its employees, officers,
            directors and agents harmless from any claim or demand, including
            reasonable attorneys' fees, made by a third party arising from the
            incorrect setting of netmasks, routes, or any other network
            configuration or programming issue which causes unnecessary
            broadcast or multicast traffic on the Websites, through the Products
            or on Company's network, or denial of service, deliberate or not,
            caused by forging address resolution protocol queries or replies or
            by configuring internet protocol addresses into Customer's hardware
            or software which were not assigned to Customer's Account. The
            foregoing actions may result in disconnection of the Products and
            suspension or termination of a Customer Account, as determined by
            Company. Any loss of functionality of a Customer Account related to
            the foregoing actions will be and remain the Customer's sole
            responsibility.
          </li>
          <li className="mb-4">
            While Company does not monitor Customer or a third-party's use of
            the Websites and/or Products to confirm compliance with the terms of
            this AUP, UToS, or any other applicable Company policy(ies), if
            Company obtains any information of Customer's violation of any such
            terms or policies, as determined in Company's sole discretion,
            Company maintains the absolute right to delete any material
            (including Customer Content) Company deems illegal, inappropriate,
            or violative of any Company or applicable third-party terms or
            policy(ies). If Company discovers a violation, it will take whatever
            action it deems reasonably necessary or appropriate to address such
            behavior, which may include (but is not limited to) disclosing
            Customer's personal information to a third party(ies) or law
            enforcement agencies and may suspend and/or terminate any agreement
            with Customer and/or Customer's access to the Products and Websites.
            CUSTOMER HEREBY WAIVES AND HOLDS COMPANY HARMLESS, ALONG WITH
            COMPANY'S EMPLOYEES, OFFICERS, DIRECTORS, AFFILIATES, LICENSEES, AND
            SERVICE PROVIDERS, FROM ANY AND ALL CLAIMS RESULTING FROM, OR
            ARISING DIRECTLY OR INDIRECTLY OUT OF, ANY ACTION TAKEN BY ANY OF
            THE FOREGOING PARTIES IN CONNECTION WITH INVESTIGATIONS BY EITHER
            COMPANY OR LAW ENFORCEMENT AUTHORITIES. In the event Company takes
            corrective action due to a violation of this AUP, Company shall not
            refund to Customer any fees paid in advance of such corrective
            action. Customer hereby agrees that Company shall have no liability
            to Customer or any of Customer's customers due to any corrective
            action that Company may take hereunder.
          </li>
          <li className="mb-4">
            Company requests that any person or entity who believes that a
            violation of this AUP or any other Company terms or policy(ies) has
            occurred may report the alleged violation(s) to Company. Such a
            report can be reported via online or by email to
            privacy@cloudvantage.com.ng. The following information should be
            included in the report:
            <ol className="list-decimal pl-6 mt-2 mb-2">
              <li className="mb-4">Name of alleged violator(s);</li>
              <li className="mb-4">
                Date and time (including time zone) of the alleged violation(s);
              </li>
              <li className="mb-4">
                The IP address(es) used to commit the alleged violation(s);
              </li>
              <li className="mb-4">Description of alleged violation(s);</li>
              <li className="mb-4">
                Evidence of the alleged violation(s) (including screenshots and
                videos); and
              </li>
              <li className="mb-4">
                The reporter's involvement, if any, in the violation (and how
                the violation was discovered in the first instance).
              </li>
            </ol>
          </li>
          <li className="mb-4">
            Any questions about this AUP can be submitted by e-mail
            (privacy@cloudvantage.com.ng) or to the Company's address.
          </li>
        </ol>
      </section>
    </div>
  );
};

export default AcceptableUsePolicy;
